import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAutoResizeTextarea } from '../hooks/useAutoResizeTextarea';
import { useRecentSearches } from '../hooks/useRecentSearches';
import type { RecentSearch } from '../repositories/savedSearchRepository';
import { SEARCH_PROMPT_MAX_CHARS } from '../searchPromptLimits';
import { isSearchSubmitShortcut } from '../utils/keyboard';
import { deduplicateRecentSearches } from '../utils/recentSearches';
import { RecentSearchClockIcon, RecentSearchesDropdown } from './RecentSearchesDropdown';

/** How a search was started from the bar: typed, or picked from the history. */
export type V2SearchMethod = 'search_box' | 'recent_search';

const HISTORY_ID = 'v2-recent-searches';

/**
 * The header's search box on the v2 reader: submitting runs the text as a
 * feeds v2 search. A signed-in reader's recent searches open under it, as the
 * snapshot reader's did: the clock button lists them, typing three or more
 * characters filters them, arrows and Enter pick one, Escape dismisses the
 * list. `children` sit at the bar's end (the phone-only controls).
 */
export function V2SearchBar({ initial = '', autoFocus = false, onSearch, children }: {
  initial?: string; autoFocus?: boolean; onSearch: (query: string, method: V2SearchMethod) => void; children?: ReactNode;
}) {
  const [query, setQuery] = useState(initial);
  const field = useRef<HTMLTextAreaElement>(null);
  useAutoResizeTextarea(field, query);
  // Read again on focus: the search the page is showing joins the history only once it starts reading,
  // after this bar mounted.
  const { items: recent, refresh } = useRecentSearches();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const historyQuery = query.trim().length >= 3 ? query : undefined;
  const recentItems = deduplicateRecentSearches(recent, 5, historyQuery);
  const popupOpen = historyOpen && recentItems.length > 0;
  // A refreshed or refiltered list may be shorter than the one a highlight was made in.
  useEffect(() => { setHighlighted(index => (index < recentItems.length ? index : -1)); }, [recentItems.length]);
  const close = () => { setHistoryOpen(false); setHighlighted(-1); };
  const submit = () => { close(); if (query.trim().length > 1) onSearch(query.trim(), 'search_box'); };
  const pick = (item: RecentSearch) => { close(); setQuery(item.description); onSearch(item.description, 'recent_search'); };
  return <form className="beta-search-bar focused" onSubmit={event => { event.preventDefault(); submit(); }}>
    <svg aria-hidden="true" className="beta-search-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10" cy="10" r="7" /><path d="m15 15 6 6" /></svg>
    <textarea ref={field} rows={1} aria-label="e.g. AI model releases" placeholder="e.g. AI model releases" value={query}
      maxLength={SEARCH_PROMPT_MAX_CHARS} autoFocus={autoFocus}
      role={recent.length ? 'combobox' : undefined}
      aria-autocomplete={recent.length ? 'list' : undefined}
      aria-expanded={recent.length ? popupOpen : undefined}
      aria-controls={popupOpen ? HISTORY_ID : undefined}
      aria-activedescendant={popupOpen && highlighted >= 0 ? `${HISTORY_ID}-${highlighted}` : undefined}
      onFocus={refresh}
      onBlur={close}
      onChange={event => { setQuery(event.target.value); if (event.target.value.trim().length >= 3) setHistoryOpen(true); setHighlighted(-1); }}
      onKeyDown={event => {
        if (event.nativeEvent.isComposing) return;
        if (popupOpen) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted(index => event.key === 'ArrowDown' ? (index + 1) % recentItems.length : (index <= 0 ? recentItems.length - 1 : index - 1));
            return;
          }
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
          if (event.key === 'Enter' && highlighted >= 0 && highlighted < recentItems.length) { event.preventDefault(); pick(recentItems[highlighted]); return; }
        }
        if (isSearchSubmitShortcut(event) || (event.key === 'Enter' && !event.shiftKey)) { event.preventDefault(); submit(); }
      }} />
    {recent.length > 0 && <button type="button" className={`beta-history-btn${popupOpen ? ' active' : ''}`} aria-label="Search history" title="Search history"
      aria-expanded={popupOpen} onMouseDown={event => event.preventDefault()}
      onClick={() => {
        // The field owns the list's keys (arrows, Enter, Escape) and its dismissal on blur, so opening it focuses the field.
        if (!historyOpen) field.current?.focus();
        setHistoryOpen(open => !open); setHighlighted(-1);
      }}><RecentSearchClockIcon /></button>}
    {children}
    <RecentSearchesDropdown id={HISTORY_ID} searches={recentItems} isOpen={popupOpen} highlightedIndex={highlighted} query={historyQuery} onSelect={pick} />
  </form>;
}
